package com.trevorwiebe.timesheet.loads.presentation.driver

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Place
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp

/**
 * Mirrors the fields on the load-confirmation papers drivers get: pickup and drop locations
 * side by side up top (tap opens maps), then the commodity and load/order/PO numbers, and
 * any special requirements, then the driver's scanned documents.
 */
@Composable
internal fun DriverLoadCard(
    load: DriverLoadCardUi,
    documentPdfs: Map<String, ByteArray>,
    isSavingDocument: Boolean,
    documentError: String?,
    onEvent: (DriverLoadsEvent) -> Unit,
    modifier: Modifier = Modifier,
) {
    Card(
        modifier = modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surface,
        ),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(12.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            SelectionContainer {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    LoadDetails(load)
                }
            }
            DriverLoadDocuments(
                loadId = load.id,
                documents = load.documents,
                documentPdfs = documentPdfs,
                isSaving = isSavingDocument,
                error = documentError,
                onEvent = onEvent,
            )
        }
    }
}

@Composable
private fun LoadDetails(load: DriverLoadCardUi) {
    Row(modifier = Modifier.fillMaxWidth()) {
        StopColumn(
            label = "Pickup",
            stops = load.pickups,
            modifier = Modifier.weight(1f),
        )
        Spacer(Modifier.width(12.dp))
        StopColumn(
            label = "Drop",
            stops = load.drops,
            modifier = Modifier.weight(1f),
        )
    }
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        DetailItem(label = "Commodity", value = load.commodity, modifier = Modifier.weight(1f))
        DetailItem(label = "Load #", value = load.loadNumber, modifier = Modifier.weight(1f))
        DetailItem(label = "Order #", value = load.orderNumber, modifier = Modifier.weight(1f))
        DetailItem(label = "PO #", value = load.po, modifier = Modifier.weight(1f))
    }
    DetailItem(label = "Requirements", value = load.customerRequirements)
}

@Composable
private fun StopColumn(
    label: String,
    stops: List<DriverStopUi>,
    modifier: Modifier = Modifier,
) {
    val uriHandler = LocalUriHandler.current
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        stops.forEach { stop ->
            val mapsLink = stop.mapsLink
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(4.dp))
                    .then(
                        if (mapsLink != null) Modifier.clickable {
                            uriHandler.openUri(mapsUrlFor(mapsLink))
                        } else Modifier
                    ),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                if (mapsLink != null) {
                    Icon(
                        Icons.Default.Place,
                        contentDescription = "Open in maps",
                        tint = MaterialTheme.colorScheme.tertiary,
                        modifier = Modifier.size(16.dp),
                    )
                    Spacer(Modifier.width(4.dp))
                }
                Column {
                    Text(
                        text = stop.name,
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = FontWeight.SemiBold,
                        color = if (mapsLink != null) MaterialTheme.colorScheme.tertiary
                        else MaterialTheme.colorScheme.onSurface,
                    )
                    if (stop.address.isNotEmpty()) {
                        Text(
                            text = stop.address,
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                    if (stop.cityState.isNotEmpty()) {
                        Text(
                            text = stop.cityState,
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun DetailItem(label: String, value: String, modifier: Modifier = Modifier) {
    if (value.isBlank()) return
    Column(modifier = modifier) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
        )
    }
}
